import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { CommonDate, DateDropdownAnchorComponent, FuzzyDate } from '@hs/ui-core-date';
import { ModalDirective, ModalOptions } from '@hs/ui-core-layout';

import { ActiveDateFormComponent } from './active-date-form/active-date-form.component';

@Component({
    selector: 'ddv-active-date-picker',
    templateUrl: './active-date-picker.component.html',
    styleUrls: ['./active-date-picker.component.scss'],
    imports: [
        ActiveDateFormComponent,
        ModalDirective,
        DateDropdownAnchorComponent,
    ],
})
export class ActiveDatePickerComponent implements AfterViewInit, OnChanges {
    @Input() mode: 'active-date' | 'date-range' = 'date-range';
    @Input() date: string | undefined;
    @Input() fuzzyDates: FuzzyDate[] = [];
    @Input() allowedRange: { fromDate: string | undefined, toDate: string | undefined } | undefined;

    @Output() modeChange = new EventEmitter<'active-date' | 'date-range'>();
    @Output() dateChange = new EventEmitter<string>();

    protected label = 'active date';
    protected placeholderText: string | undefined;
    protected minDate: CommonDate | undefined;
    protected maxDate: CommonDate | undefined;
    protected showModal = false;
    protected modalOptions: ModalOptions = {};
    protected activeDate: CommonDate | FuzzyDate | undefined;
    protected isDirty = false;

    @ViewChild('anchor', { static: false, read: ElementRef }) private readonly anchor: ElementRef<HTMLElement> | undefined;

    ngAfterViewInit(): void {
        this.modalOptions = {
            anchorToElement: this.anchor?.nativeElement,
            position: 'bottom-right',
        };
    }

    ngOnChanges(): void {
        this.setActiveDateForModeAndDate();
        this.setMinAndMaxDates();

        this.placeholderText = this.mode === 'date-range' ? 'RANGE' : undefined;

        if (this.mode === 'active-date' && this.activeDate instanceof FuzzyDate) {
            this.label = 'dynamic active';
        } else {
            this.label = 'active date';
        }
    }

    protected toggleList(): void {
        if (this.showModal) {
            this.closeList();
        } else {
            this.showModal = true;
        }
    }

    protected closeList(): void {
        this.showModal = false;

        if (this.isDirty) {
            this.emitDateChange();
            this.isDirty = false;
        }
    }

    protected onModeChange(): void {
        if (this.mode === 'date-range') {
            this.activeDate = undefined;
        } else {
            this.activeDate = this.fuzzyDates.find((fd) => fd.name === 'Yesterday');
        }

        this.modeChange.emit(this.mode);
    }

    private setActiveDateForModeAndDate(): void {
        if (this.mode === 'date-range' || !this.date) {
            this.activeDate = undefined;
        } else {
            const fuzzy = this.lookupFuzzyDateByName(this.date);
            if (fuzzy) {
                this.activeDate = fuzzy;
            } else {
                this.activeDate = CommonDate.fromUSFormat(this.date);
            }
        }
    }

    private setMinAndMaxDates(): void {
        this.minDate = this.getCommonDateFromString(this.allowedRange?.fromDate);
        this.maxDate = this.getCommonDateFromString(this.allowedRange?.toDate);
    }

    private getCommonDateFromString(value: string | undefined): CommonDate | undefined {
        if (!value) {
            return undefined;
        }

        const fuzzy = this.lookupFuzzyDateByName(value);
        if (fuzzy) {
            return fuzzy.commonDate;
        }

        return CommonDate.fromUSFormat(value);
    }

    private lookupFuzzyDateByName(name: string): FuzzyDate | undefined {
        return this.fuzzyDates.find((fd) => fd.name === name);
    }

    private emitDateChange(): void {
        if (!this.activeDate) {
            return;
        }

        if (this.activeDate instanceof CommonDate) {
            this.dateChange.emit(this.activeDate.toNamedFormat('us'));
        } else {
            this.dateChange.emit(this.activeDate.name);
        }
    }
}
